import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import AuthLink from '../components/AuthLink';
import { getRedirectUrl } from '../utils/redirectHelpers';

interface RegisterProps {
  onLoginSuccess?: (token: string) => void;
}

const Register: React.FC<RegisterProps> = ({ onLoginSuccess }) => {
  const { t, currentLanguage } = useTranslation();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'form' | 'verify'>('form');

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePassword = (password: string) => password.length >= 7 && /[A-Z]/.test(password) && /\d/.test(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!validateEmail(email)) {
      setError(t('errors.invalid_email'));
      return;
    }
    if (!firstName || !lastName) {
      setError(t('validation.all_fields_required'));
      return;
    }
    if (!validatePassword(password)) {
      setError(t('validation.password_requirements'));
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`/api/register?lang=${currentLanguage}`, { email, firstName, lastName, password });
      const continuePurchase = Boolean(getRedirectUrl());
      if (continuePurchase && onLoginSuccess) {
        try {
          const loginResponse = await axios.post('/api/login', { email, password });
          onLoginSuccess(loginResponse.data.token);
          return;
        } catch {
          setError(t('errors.invalid_credentials'));
        }
      }
      setMessage(t('email_verification.sent'));
      if (response.data?.emailSent === false) {
        setError(t('email_verification.send_failed'));
      }
      setStep('verify');
    } catch (err: any) {
      setError(t('errors.registration_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-8">
      <div className="w-full max-w-md mx-auto bg-gray-800/50 backdrop-blur-lg border border-gray-700 rounded-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {step === 'verify' ? t('email_verification.title') : t('registration.create_account_title')}
          </h1>
          <p className="text-gray-400">
            {step === 'verify' ? t('email_verification.subtitle') : t('registration.join_vst_shop')}
          </p>
        </div>
        
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}
        
        {message && (
          <div className="bg-green-500/20 border border-green-500/50 text-green-300 px-4 py-3 rounded-md mb-6">
            {message}
          </div>
        )}
        
        {step === 'verify' ? (
          <div className="space-y-6">
            <button
              type="button"
              disabled={loading}
              onClick={async () => {
                setError('');
                setLoading(true);
                try {
                  await axios.post(`/api/verify-email/resend?lang=${currentLanguage}`, { email });
                  setMessage(t('email_verification.sent'));
                } catch {
                  setError(t('email_verification.send_failed'));
                } finally {
                  setLoading(false);
                }
              }}
              className="w-full text-purple-300 hover:text-white"
            >
              {t('email_verification.resend_button')}
            </button>
            <Link to="/login" className="block text-center text-gray-300 hover:text-white">
              {t('email_verification.go_to_login')}
            </Link>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('auth.email_label')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
              placeholder={t('auth.email_placeholder')}
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('registration.first_name_label')}
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                placeholder={t('registration.first_name_placeholder')}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('registration.last_name_label')}
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                placeholder={t('registration.last_name_placeholder')}
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('auth.password_label')}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
              placeholder={t('auth.password_placeholder')}
              required
            />
            <p className="text-xs text-gray-400 mt-1">
              {t('validation.password_requirements')}
            </p>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-600 text-white font-semibold py-3 px-4 rounded-md transition-all duration-300 disabled:cursor-not-allowed"
          >
            {loading ? t('registration.registering') : t('registration.register_button')}
          </button>
        </form>
        )}
        
        <div className="mt-8 text-center">
          <AuthLink 
            to="/login" 
            className="text-purple-400 hover:text-purple-300 transition-colors"
          >
            {t('auth.have_account_login')}
          </AuthLink>
        </div>
      </div>
    </div>
  );
};

export default Register; 